"use client"

import { useCallback, useEffect, useState } from "react"
import api from "../utils/api"
import Icon from "./Icon"
import { getApiErrorMessage } from "../utils/errorMessage"
import {
  formatMoney,
  formatDateWithTime,
  formatDateInputValue,
  getNairobiMonth,
  getNairobiYear,
} from "../utils/format"
import { GLOBAL_WALLET_MODULES } from "./GlobalWalletCard"

const MODULE_LABELS = GLOBAL_WALLET_MODULES.reduce((acc, m) => {
  acc[m.key] = m.label
  return acc
}, {})

// Previous + current month range in YYYY-MM-DD (Africa/Nairobi), matching the
// default range used by the per-module wallet statements on the dashboard.
const getPreviousAndCurrentMonthRange = () => {
  const y = getNairobiYear()
  const m = getNairobiMonth()
  const start = new Date(y, m - 2, 1)
  const end = new Date(y, m, 0)
  return { start: formatDateInputValue(start), end: formatDateInputValue(end) }
}

// Combined statement of money in/out across all of the user's module wallets.
function GlobalWalletTransactionsModal({ onClose }) {
  const [transactions, setTransactions] = useState([])
  const [modules, setModules] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [startDate, setStartDate] = useState("")
  const [endDate, setEndDate] = useState("")
  const [filterOpen, setFilterOpen] = useState(false)

  const fetchTransactions = useCallback(async (start = "", end = "") => {
    try {
      setLoading(true)
      setError("")
      const { start: defaultStart, end: defaultEnd } = getPreviousAndCurrentMonthRange()
      const effectiveStart = start || defaultStart
      const effectiveEnd = end || defaultEnd
      setStartDate(effectiveStart)
      setEndDate(effectiveEnd)

      const params = new URLSearchParams()
      if (effectiveStart) params.append("startDate", effectiveStart)
      if (effectiveEnd) params.append("endDate", effectiveEnd)

      const response = await api.get(`/wallet/global/transactions?${params.toString()}`)
      setTransactions(Array.isArray(response?.data?.transactions) ? response.data.transactions : [])
      setModules(Array.isArray(response?.data?.modules) ? response.data.modules : [])
      setTotal(Number(response?.data?.total || 0))
    } catch (err) {
      console.error("Error fetching global wallet transactions:", err)
      setError(getApiErrorMessage(err, "Failed to fetch global wallet transactions."))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchTransactions()
  }, [fetchTransactions])

  const moduleBalances = modules.reduce((acc, m) => {
    acc[m.key] = Number(m.balance || 0)
    return acc
  }, {})

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-green-700 p-4 sm:p-6 text-white flex-shrink-0">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-xl sm:text-2xl font-bold mb-2">Global Wallet Statement</h3>
              <div className="text-sm opacity-90">
                <span className="font-medium">Combined Balance: {formatMoney(total)}</span>
              </div>
              <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs opacity-75">
                {modules.map((m) => (
                  <span key={m.key}>
                    {m.label}: {formatMoney(m.balance)}
                  </span>
                ))}
              </div>
            </div>
            <button
              onClick={onClose}
              className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-lg p-2 transition-colors"
            >
              <Icon name="x" size="1.2rem" />
            </button>
          </div>
        </div>

        {/* Date Filter Section */}
        <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gray-50 border-b border-gray-200 flex-shrink-0">
          <button
            onClick={() => setFilterOpen((prev) => !prev)}
            className="w-full flex items-center justify-between text-gray-800 font-medium"
          >
            <span>Filter</span>
            <Icon name={filterOpen ? "chevron-up" : "chevron-down"} size="1rem" className="text-gray-600 sm:w-5 sm:h-5" />
          </button>
          {filterOpen && (
            <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:gap-4 sm:items-end">
              <div className="flex-1">
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div className="flex-1">
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">End Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div className="flex gap-2 w-full sm:w-auto">
                <button
                  onClick={() => fetchTransactions(startDate, endDate)}
                  className="flex-1 sm:flex-none bg-green-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-green-700 transition-colors font-medium text-sm"
                >
                  Filter
                </button>
                <button
                  onClick={() => fetchTransactions()}
                  className="flex-1 sm:flex-none bg-gray-500 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors font-medium text-sm"
                >
                  Clear
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="p-4 sm:p-6">
            {loading ? (
              <div className="text-center py-8">
                <p className="text-gray-500">Loading transactions...</p>
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            ) : transactions.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500 text-sm">No transactions found.</p>
              </div>
            ) : (
              <table className="w-full table-fixed border-collapse border border-gray-300 text-[10px] leading-tight sm:text-sm">
                <colgroup>
                  <col style={{ width: "14%" }} />
                  <col style={{ width: "30%" }} />
                  <col style={{ width: "12%" }} />
                  <col style={{ width: "14%" }} />
                  <col style={{ width: "14%" }} />
                  <col style={{ width: "16%" }} />
                </colgroup>
                <thead>
                  <tr className="bg-gray-50">
                    <th className="border border-gray-300 px-1 py-1 text-left font-medium text-gray-700 sm:px-4 sm:py-2">Date</th>
                    <th className="border border-gray-300 px-1 py-1 text-left font-medium text-gray-700 sm:px-4 sm:py-2">Description</th>
                    <th className="border border-gray-300 px-1 py-1 text-left font-medium text-gray-700 sm:px-4 sm:py-2">Wallet</th>
                    <th className="border border-gray-300 px-1 py-1 text-right font-medium text-gray-700 sm:px-4 sm:py-2">Money In</th>
                    <th className="border border-gray-300 px-1 py-1 text-right font-medium text-gray-700 sm:px-4 sm:py-2">Money Out</th>
                    <th className="border border-gray-300 px-1 py-1 text-right font-medium text-gray-700 sm:px-4 sm:py-2">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((tx, index) => {
                    const amount = Number(tx?.amount || 0)
                    const hasBalanceSnapshot =
                      tx?.balanceAfter !== null &&
                      tx?.balanceAfter !== undefined &&
                      Number.isFinite(Number(tx.balanceAfter))
                    let balanceAfterTransaction = hasBalanceSnapshot
                      ? Number(tx.balanceAfter)
                      : Number(moduleBalances[tx.module] || 0)
                    if (!hasBalanceSnapshot) {
                      for (let i = 0; i < index; i++) {
                        if (transactions[i].module === tx.module) {
                          balanceAfterTransaction -= Number(transactions[i].amount || 0)
                        }
                      }
                    }
                    return (
                      <tr key={tx._id || index} className="hover:bg-gray-50">
                        <td className="border border-gray-300 px-1 py-1 align-top break-words text-gray-700 sm:px-4 sm:py-2">
                          {formatDateWithTime(tx.timestamp)}
                        </td>
                        <td className="border border-gray-300 px-1 py-1 align-top break-words sm:px-4 sm:py-2">
                          {tx.description}
                        </td>
                        <td className="border border-gray-300 px-1 py-1 align-top break-words sm:px-4 sm:py-2">
                          {tx.moduleLabel || MODULE_LABELS[tx.module] || tx.module}
                        </td>
                        <td className="border border-gray-300 px-1 py-1 align-top break-words text-right font-medium text-green-600 sm:px-4 sm:py-2">
                          {amount > 0 ? formatMoney(amount) : ""}
                        </td>
                        <td className="border border-gray-300 px-1 py-1 align-top break-words text-right font-medium text-red-600 sm:px-4 sm:py-2">
                          {amount < 0 ? formatMoney(Math.abs(amount)) : ""}
                        </td>
                        <td className="border border-gray-300 px-1 py-1 align-top break-words text-right font-medium text-gray-700 sm:px-4 sm:py-2">
                          {formatMoney(balanceAfterTransaction)}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default GlobalWalletTransactionsModal
